import get from 'lodash/get';
import set from 'lodash/set';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  getInitialState,
  updateComponent,
} from '~/app/components/dynamic-grid/dynamic-grid-helpers';
import { useBuilder } from '~/app/lib/redux/selectors/builder-selector';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';

export const IMAGE_SOURCE_TYPES = {
  MEDIA_LIBRARY: 'media-library',
  CUSTOM_URL: 'custom-url',
  MARKETPLACE_OFFER: 'marketplace-offer',
};

export const useDynamicImageForm = () => {
  const dispatch = useDispatch();
  const { flatOutline, selected: selectedComponentId } = useBuilder();
  const { componentPath, marketplaceOffers } = useDynamicGrid({
    componentId: selectedComponentId,
  });

  const currentComponent = flatOutline?.[selectedComponentId];
  const initialImageSource = get(
    currentComponent,
    `props.${componentPath}.imageSource`,
    'custom-url'
  );

  const initialCustomUrl = get(
    currentComponent,
    `props.${componentPath}.imageUrl`,
    ''
  );

  const [tabIndex, setTabIndex] = useState(0);
  const [imageSource, setImageSource] = useState(initialImageSource);
  const [circularImage, setCircularImage] = useState(false);
  const [customUrl, setCustomUrl] = useState('');
  const [rolloverImageUrl, setRolloverImageUrl] = useState('');
  const [imageLinkUrl, setImageLinkUrl] = useState('');
  const [objectFit, setObjectFit] = useState('cover');
  const [height, setHeight] = useState('');
  const [width, setWidth] = useState('');
  const [marketplaceOfferId, setMarketplaceOfferId] = useState('');
  const [altText, setAltText] = useState('');
  const [objectPosition, setObjectPosition] = useState('center');
  const [minHeight, setMinHeight] = useState('');
  const [minWidth, setMinWidth] = useState('');
  const [maxWidth, setMaxWidth] = useState('');
  const [maxHeight, setMaxHeight] = useState('');
  const [enableFetchPriority, setEnableFetchPriority] = useState(false);
  const [captureCustomQueryParams, setCaptureCustomQueryParams] =
    useState(false);

  const imageSourceOptions = useCallback(() => {
    const options = [
      {
        label: 'Custom URL',
        value: IMAGE_SOURCE_TYPES.CUSTOM_URL,
      },
      {
        label: 'WordPress Media Library',
        value: IMAGE_SOURCE_TYPES.MEDIA_LIBRARY,
      },
    ];

    if (!marketplaceOffers?.length) {
      return options;
    }

    options.push({
      label: 'Marketplace Offer',
      value: IMAGE_SOURCE_TYPES.MARKETPLACE_OFFER,
    });

    return options;
  }, [marketplaceOffers]);

  const marketplaceOfferOptions = useCallback(() => {
    const options = marketplaceOffers?.map((offer: any) => ({
      label: offer.name,
      value: offer.id,
    }));
    options.unshift({
      label: 'Select a Marketplace Offer',
      value: '',
    });
    return options;
  }, [marketplaceOffers]);

  const handleUpdateComponent = (field: string, value: string | boolean) => {
    set(currentComponent, `props.${componentPath}.${field}`, value);
    dispatch(updateComponent(currentComponent));
  };

  const handleSetCustomUrl = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    handleUpdateComponent('imageUrl', value);
    setCustomUrl(value);
  };

  const handleSetRolloverImageUrl = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;
    handleUpdateComponent('rolloverImageUrl', value);
    setRolloverImageUrl(value);
  };

  const handleSetImageLinkUrl = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    handleUpdateComponent('imageLinkUrl', value);
    setImageLinkUrl(value);
  };

  const handleSetHeight = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    handleUpdateComponent('height', value);
    setHeight(value);
  };

  const handleSetWidth = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    handleUpdateComponent('width', value);
    setWidth(value);
  };

  const handleSetObjectFit = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    handleUpdateComponent('objectFit', value);
    setObjectFit(value);
  };

  const handleSetImageSource = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    handleUpdateComponent('imageSource', value);
    setImageSource(value);
    dispatch(updateComponent(currentComponent));
  };

  const handleSetCircularImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCircularImage(e.target.checked);
    handleUpdateComponent('circularImage', e.target.checked);
  };

  const handleSetAltText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    handleUpdateComponent('altText', value);
    setAltText(value);
  };

  const handleSetObjectPosition = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    setObjectPosition(value);
    handleUpdateComponent('objectPosition', value);
  };

  const handleSetMinHeight = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setMinHeight(value);
    handleUpdateComponent('minHeight', value);
  };

  const handleSetMinWidth = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setMinWidth(value);
    handleUpdateComponent('minWidth', value);
  };

  const handleSetMaxWidth = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setMaxWidth(value);
    handleUpdateComponent('maxWidth', value);
  };

  const handleSetMaxHeight = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setMaxHeight(value);
    handleUpdateComponent('maxHeight', value);
  };

  const handleSetEnableFetchPriority = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEnableFetchPriority(e.target.checked);
    handleUpdateComponent('enableFetchPriority', e.target.checked);
  };

  const handleCaptureCustomQueryParamsChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCaptureCustomQueryParams(e.target.checked);
    handleUpdateComponent('captureCustomQueryParams', e.target.checked);
  };

  const handleSetMarketplaceOffer = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { value: selectedOfferId } = e.target;
    const selectedOffer = marketplaceOffers?.find(
      (offer: any) => offer.id === selectedOfferId
    );
    const selectedOfferImageSource = selectedOffer?.image?.source;
    setMarketplaceOfferId(selectedOfferId);
    setCustomUrl(selectedOfferImageSource);
    handleUpdateComponent('marketplaceOfferId', selectedOfferId);
    handleUpdateComponent('imageUrl', selectedOfferImageSource);
    dispatch(updateComponent(currentComponent));
  };

  const handleSelectImageSourceCallback = ({
    imageSource: imageSourceType,
  }: {
    imageUrl?: string;
    imageSource?: string;
  }) => {
    setImageSource(imageSourceType);
    set(
      currentComponent,
      `props.${componentPath}.imageSource`,
      imageSourceType
    );
  };

  useEffect(() => {
    setImageSource(initialImageSource);
    setCustomUrl(
      getInitialState(currentComponent, componentPath, 'imageUrl', '')
    );
    setRolloverImageUrl(
      getInitialState(currentComponent, componentPath, 'rolloverImageUrl', '')
    );
    setImageLinkUrl(
      getInitialState(currentComponent, componentPath, 'imageLinkUrl', '')
    );
    setHeight(getInitialState(currentComponent, componentPath, 'height', ''));
    setWidth(getInitialState(currentComponent, componentPath, 'width', ''));
    setObjectFit(
      getInitialState(currentComponent, componentPath, 'objectFit', 'cover')
    );
    setMarketplaceOfferId(
      getInitialState(currentComponent, componentPath, 'marketplaceOfferId', '')
    );
    setCircularImage(
      getInitialState(currentComponent, componentPath, 'circularImage', false)
    );
    setAltText(getInitialState(currentComponent, componentPath, 'altText', ''));
    setObjectPosition(
      getInitialState(
        currentComponent,
        componentPath,
        'objectPosition',
        'center'
      )
    );
    setMinHeight(
      getInitialState(currentComponent, componentPath, 'minHeight', '')
    );
    setMinWidth(
      getInitialState(currentComponent, componentPath, 'minWidth', '')
    );
    setMaxWidth(
      getInitialState(currentComponent, componentPath, 'maxWidth', '')
    );
    setMaxHeight(
      getInitialState(currentComponent, componentPath, 'maxHeight', '')
    );
    setEnableFetchPriority(
      getInitialState(
        currentComponent,
        componentPath,
        'enableFetchPriority',
        false
      )
    );
    setCaptureCustomQueryParams(
      getInitialState(
        currentComponent,
        componentPath,
        'captureCustomQueryParams',
        false
      )
    );
  }, [
    currentComponent,
    initialImageSource,
    componentPath,
    setCircularImage,
    setCustomUrl,
    setHeight,
    setImageSource,
    setMarketplaceOfferId,
    setObjectFit,
    setWidth,
    setObjectPosition,
    setAltText,
    setRolloverImageUrl,
    setImageLinkUrl,
  ]);

  return {
    initialCustomUrl,
    tabIndex,
    imageSource,
    circularImage,
    customUrl,
    imageLinkUrl,
    rolloverImageUrl,
    objectFit,
    height,
    width,
    objectPosition,
    handleSetAltText,
    marketplaceOfferId,
    altText,
    imageSourceOptions,
    marketplaceOfferOptions,
    handleSetCircularImage,
    handleSetCustomUrl,
    handleSetRolloverImageUrl,
    handleSetImageLinkUrl,
    handleSetHeight,
    handleSetWidth,
    handleSetObjectFit,
    handleSetImageSource,
    handleSetMarketplaceOffer,
    handleSelectImageSourceCallback,
    setTabIndex,
    currentComponent,
    handleSetObjectPosition,
    handleSetMinHeight,
    handleSetMinWidth,
    minHeight,
    minWidth,
    maxWidth,
    maxHeight,
    handleSetMaxWidth,
    handleSetMaxHeight,
    handleSetEnableFetchPriority,
    enableFetchPriority,
    captureCustomQueryParams,
    handleCaptureCustomQueryParamsChange,
  };
};
